import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeLatest, getContext, put, call} from 'redux-saga/effects'
import Api, {UnauthorizedError} from 'Api'
import {AlertMessageData} from '../../components/AlertMessage'

export type ImportCsvState = {
  uploading: boolean
  error?: AlertMessageData
  successMessage?: string
}

export const initialState: ImportCsvState = {
  uploading: false,
  error: undefined,
  successMessage: undefined,
}

export const actions = {
  upload: createAction<{file: File}>('importCsv/upload'),
  reset: createAction<void>('importCsv/reset'),
  setUploading: createAction<{uploading: boolean}>('importCsv/setUploading'),
  setError: createAction<{error?: AlertMessageData}>('importCsv/setError'),
  setSuccessMessage: createAction<{message?: string}>('importCsv/setSuccessMessage'),
}

export const reducer = createReducer(initialState, builder => {
  builder
    .addCase(actions.reset, () => ({...initialState}))
    .addCase(actions.setUploading, (state, action: ReturnType<typeof actions.setUploading>) => {
      state.uploading = action.payload.uploading
    })
    .addCase(actions.setError, (state, action: ReturnType<typeof actions.setError>) => {
      state.error = action.payload.error as AlertMessageData | undefined
    })
    .addCase(
      actions.setSuccessMessage,
      (state, action: ReturnType<typeof actions.setSuccessMessage>) => {
        state.successMessage = action.payload.message
      },
    )
})

const uploadSaga = function*(action: ReturnType<typeof actions.upload>) {
  const {file} = action.payload
  const api: Api = yield getContext('api')

  yield put(actions.setUploading({uploading: true}))
  yield put(actions.setError({error: undefined}))
  yield put(actions.setSuccessMessage({message: undefined}))

  try {
    yield call(api.uploadCsv, file)
    yield put(actions.setSuccessMessage({message: `"${file.name}" was processed successfully.`}))
  } catch (error) {
    if (error === UnauthorizedError) {
      throw error
    }
    const message =
      error && (error as any).message
        ? (error as any).message
        : 'Unknown error occurred during CSV upload.'
    yield put(actions.setError({error: message}))
  } finally {
    yield put(actions.setUploading({uploading: false}))
  }
}

export const saga = function*() {
  yield takeLatest(actions.upload, uploadSaga)
}
